import { useQuery } from '@tanstack/react-query';
import {
  Archive,
  Bell,
  ChevronDown,
  ChevronRight,
  FolderKanban,
  LayoutDashboard,
  type LucideIcon,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Settings,
  ShieldCheck,
  User,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { listProjects } from '@/lib/project-api';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  match?: 'exact' | 'prefix';
}

function buildNav(orgSlug: string | null, isSuperAdmin: boolean): NavItem[] {
  if (isSuperAdmin) {
    return [
      { to: '/super-admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/super-admin/organizations', label: 'Organizations', icon: FolderKanban },
      { to: '/super-admin/invitations', label: 'Invitations', icon: Bell },
    ];
  }
  if (!orgSlug) {
    return [{ to: '/profile', label: 'Profile', icon: User }];
  }
  return [
    { to: `/o/${orgSlug}/dashboard`, label: 'Dashboard', icon: LayoutDashboard },
    { to: `/o/${orgSlug}/projects`, label: 'Projects', icon: FolderKanban },
    { to: `/o/${orgSlug}/settings`, label: 'Settings', icon: Settings },
  ];
}

export function Sidebar() {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggle = useUIStore((s) => s.toggleSidebar);
  const user = useAuthStore((s) => s.user);
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const location = useLocation();

  const activeMembership = user?.memberships.find((m) => m.organizationId === activeOrgId) ?? null;
  const orgSlug = activeMembership?.organizationSlug ?? null;

  const nav = user ? buildNav(orgSlug, user.isSuperAdmin) : [];

  // Only fetch projects for regular org members (not super-admins on their own
  // dashboard routes, and not when there's no active org).
  const projectsQuery = useQuery({
    queryKey: ['org', activeOrgId, 'projects', 'sidebar'],
    queryFn: () => listProjects(activeOrgId!, { includeArchived: false }),
    enabled: Boolean(activeOrgId) && !user?.isSuperAdmin,
  });

  const projects = useMemo(() => {
    const items = projectsQuery.data?.items ?? [];
    // Sort alphabetically by name for a stable nav order.
    return items
      .filter((p) => !p.archivedAt)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projectsQuery.data?.items]);

  // The currently-open project key (if we're inside a project route); used to
  // auto-expand the Projects section and highlight the active row.
  const activeProjectKey = useMemo(() => {
    const match = location.pathname.match(/\/o\/[^/]+\/projects\/([^/]+)/);
    return match?.[1] ?? null;
  }, [location.pathname]);

  const [projectsExpanded, setProjectsExpanded] = useState<boolean>(true);
  useEffect(() => {
    // Whenever the user navigates into a project, make sure the section is
    // expanded so the active row is visible.
    if (activeProjectKey) setProjectsExpanded(true);
  }, [activeProjectKey]);

  const showProjectsTree = Boolean(orgSlug) && !user?.isSuperAdmin && !collapsed;

  return (
    <aside
      data-collapsed={collapsed}
      className={cn(
        'flex h-full shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200',
        collapsed ? 'w-16' : 'w-64',
      )}
      aria-label="Primary"
    >
      <div
        className={cn(
          'flex h-14 items-center gap-2 border-b border-sidebar-border px-3',
          collapsed && 'justify-center px-2',
        )}
      >
        <div className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground text-sm font-bold">
          J
        </div>
        {!collapsed ? (
          <span className="text-sm font-semibold tracking-wide">Jira Clone</span>
        ) : null}
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto p-2">
        {user?.isSuperAdmin ? (
          <div
            className={cn(
              'mb-2 flex items-center gap-2 rounded-md bg-amber-500/10 px-2 py-1.5 text-xs font-medium text-amber-400',
              collapsed && 'justify-center px-0',
            )}
          >
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
            {!collapsed ? <span>Super admin</span> : null}
          </div>
        ) : null}

        {nav.map((item) => {
          const isProjects =
            orgSlug !== null && item.to === `/o/${orgSlug}/projects`;

          if (isProjects && showProjectsTree) {
            return (
              <ProjectsNavGroup
                key={item.to}
                to={item.to}
                label={item.label}
                icon={item.icon}
                expanded={projectsExpanded}
                onToggle={() => setProjectsExpanded((v) => !v)}
                projects={projects}
                loading={projectsQuery.isLoading}
                orgSlug={orgSlug!}
                activeProjectKey={activeProjectKey}
              />
            );
          }

          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.match === 'exact'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-foreground'
                    : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground',
                  collapsed && 'justify-center px-0',
                )
              }
              title={collapsed ? item.label : undefined}
            >
              <item.icon className="h-4 w-4 shrink-0" aria-hidden />
              {!collapsed ? <span className="truncate">{item.label}</span> : null}
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-sidebar-border p-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggle}
          className={cn(
            'w-full justify-start gap-2 text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground',
            collapsed && 'justify-center',
          )}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
        >
          {collapsed ? (
            <PanelLeftOpen className="h-4 w-4" aria-hidden />
          ) : (
            <>
              <PanelLeftClose className="h-4 w-4" aria-hidden />
              <span className="text-xs">Collapse</span>
            </>
          )}
        </Button>
      </div>
    </aside>
  );
}

// -------------------- Projects tree --------------------

type SidebarProject = {
  id: string;
  name: string;
  key: string;
  archivedAt: string | null;
};

function ProjectsNavGroup({
  to,
  label,
  icon: Icon,
  expanded,
  onToggle,
  projects,
  loading,
  orgSlug,
  activeProjectKey,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  expanded: boolean;
  onToggle: () => void;
  projects: SidebarProject[];
  loading: boolean;
  orgSlug: string;
  activeProjectKey: string | null;
}) {
  return (
    <div className="space-y-1">
      {/* Header row: chevron toggle + nav link */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
          className="flex h-7 w-6 shrink-0 items-center justify-center rounded-md text-sidebar-foreground/60 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
        >
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
        <NavLink
          to={to}
          end
          className={({ isActive }) =>
            cn(
              'flex flex-1 items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'bg-sidebar-accent text-sidebar-foreground'
                : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground',
            )
          }
        >
          <Icon className="h-4 w-4 shrink-0" aria-hidden />
          <span className="truncate">{label}</span>
          {projects.length > 0 ? (
            <span className="ml-auto rounded-full bg-sidebar-accent px-1.5 py-0 text-[10px] font-semibold tabular-nums text-sidebar-foreground/70">
              {projects.length}
            </span>
          ) : null}
        </NavLink>
      </div>

      {/* Project list */}
      {expanded ? (
        <ul className="ml-6 space-y-0.5 border-l border-sidebar-border/60 pl-2">
          {loading && projects.length === 0 ? (
            <>
              <SidebarSkeleton />
              <SidebarSkeleton />
              <SidebarSkeleton />
            </>
          ) : projects.length === 0 ? (
            <li className="px-2 py-1.5 text-[11px] italic text-sidebar-foreground/50">
              No projects yet
            </li>
          ) : (
            projects.map((p) => {
              const projectPath = `/o/${orgSlug}/projects/${p.key}`;
              const isActive = activeProjectKey === p.key;
              const archived = Boolean(p.archivedAt);
              return (
                <li key={p.id}>
                  <Link
                    to={projectPath}
                    className={cn(
                      'group flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] transition-colors',
                      isActive
                        ? 'bg-sidebar-accent text-sidebar-foreground'
                        : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
                    )}
                    title={`${p.key} · ${p.name}`}
                  >
                    <span
                      className={cn(
                        'inline-flex h-5 shrink-0 items-center rounded bg-sidebar-accent px-1.5 font-mono text-[9px] font-semibold uppercase tracking-wide',
                        isActive ? 'text-sidebar-foreground' : 'text-sidebar-foreground/60',
                      )}
                    >
                      {p.key}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{p.name}</span>
                    {archived ? (
                      <Archive
                        className="h-3 w-3 shrink-0 text-sidebar-foreground/40"
                        aria-hidden
                      />
                    ) : null}
                  </Link>
                </li>
              );
            })
          )}
          <li>
            <Link
              to={to}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[11px] font-medium text-sidebar-foreground/50 transition-colors hover:bg-sidebar-accent/60 hover:text-sidebar-foreground"
            >
              <Plus className="h-3 w-3" aria-hidden />
              New project
            </Link>
          </li>
        </ul>
      ) : null}
    </div>
  );
}

function SidebarSkeleton() {
  return (
    <li
      className="mx-2 my-1 h-5 animate-pulse rounded bg-sidebar-accent/40"
      aria-hidden
    />
  );
}
